import React, { Component } from "react";
import { WithTranslation, withTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import styled from "styled-components";
import { GarageKeyType } from "src/components/UserAccount/shared/GarageKey";
import GarageHeader from "../shared/GarageHeader";
import WaityButton from "../shared/WaityButton";
import * as garageCode from "./icon-garage-code.svg";

const KEY_DIGITS = [0, 1, 2, 3, 4];

const Root = styled("div")`
    font-size: 0.9em;
    margin: 0.25em auto 1em auto;
    margin: 50px auto 28px;
    max-width: 550px;
    overflow-y: hidden;
    width: 90%;
`;

const Container = styled("div")`
    margin: 0 auto 40px;
    text-align: center;
`;

const Input = styled("input")`
    height: 60px;
    margin: 5px;
    padding: 0.7em 0.4em;
    text-align: center;
    width: 50px;

    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
        /* stylelint-disable */
        -webkit-appearance: none;
        /* stylelint-enable */
        margin: 0;
    }

    &:first-of-type {
        margin-left: 0;
    }

    &:last-of-type {
        margin-right: 0;
    }
`;

const LinkContainer = styled("div")`
    cursor: pointer;
    display: flex;
    flex-direction: column;
    font-size: 14px;
    justify-content: space-between;
    margin: 1.5em;
    text-align: center;
    text-decoration: underline;

    ${(props) => props.theme.breakpoints.down("md")} {
        height: 50px;
    }

    ${(props) => props.theme.breakpoints.up("md")} {
        flex-direction: row;
        margin: 30px 40px;
    }
`;

const StyledLink = styled(Link)`
    color: #4a4a4a;
`;

const Description = styled("div")<{ message: string }>`
    margin-bottom: ${(props) => (props.message ? "20px" : "40px")};
`;

const Message = styled("div")<{ hasError: boolean }>`
    border: 1px solid ${(props) => (props.hasError ? props.theme.palette.error.main : props.theme.palette.success.main)};
    color: ${(props) => (props.hasError ? props.theme.palette.error.main : props.theme.palette.success.main)};
    font-weight: bold;
    margin: 0.4em auto;
    padding: 0.4em 1.5em;
    text-align: center;
`;

export interface UnlockGarageProps {
    code: string[];
    message: string;
    garageKey: string;
    garageKeyType: GarageKeyType;
    hasError: boolean;
    onSuccess: () => void;
    resendCode: () => void;
    resetCode: (value: string) => void;
    setCodeByIndex: (index: number, value: string) => void;
    submitUnlock: () => Promise<any>;
}
class UnlockGarage extends Component<UnlockGarageProps & WithTranslation> {
    public keyRefs: any[];
    constructor(props: any) {
        super(props);

        this.keyRefs = [];
    }
    public componentDidMount() {
        this.keyRefs[0] && this.keyRefs[0].focus();
    }
    public render() {
        const {
            code,
            garageKey,
            garageKeyType,
            message,
            submitUnlock,
            onSuccess,
            resendCode,
            hasError,
            t,
        } = this.props;

        const keyDigits = KEY_DIGITS.map((i) => {
            return (
                <Input
                    autoComplete="off"
                    data-index={i}
                    id={`unlockKeyDigit${i}`}
                    key={`unlockKeyDigit${i}`}
                    name="GarageKey"
                    onKeyUp={this.moveUp(i)}
                    onChange={this.reportUp(i)}
                    type="tel"
                    ref={this.setKeyRef}
                    value={(code && code[i]) || ""}
                />
            );
        });

        return (
            <Root>
                <GarageHeader text={t("UserAccount.UnlockGarage.title")} icon={garageCode} />
                <Container>
                    <Description message={message}>
                        {garageKeyType === GarageKeyType.email
                            ? t("UserAccount.UnlockGarage.descriptionEmail", { garageKey })
                            : t("UserAccount.UnlockGarage.descriptionPhone", { garageKey })}
                    </Description>
                    {message && <Message hasError={hasError}>{message}</Message>}
                    {keyDigits}
                </Container>
                <div>
                    <WaityButton
                        onClick={submitUnlock}
                        onSuccess={onSuccess}
                        text={t("UserAccount.UnlockGarage.unlockButtonLabel")}
                    />
                </div>
                <LinkContainer>
                    <div onClick={resendCode}>{t("UserAccount.UnlockGarage.sendAgain")}</div>
                    <StyledLink to="/access-my-garage">{t("UserAccount.UnlockGarage.wrongInformation")}</StyledLink>
                </LinkContainer>
            </Root>
        );
    }

    private setKeyRef = (element: any) => {
        this.keyRefs.push(element);
    };

    private moveUp = (index: number) => (e: any) => {
        const keynum = e.keyCode || e.which;
        const value = this.keyRefs[index].value;
        if (value !== undefined && keynum === 8 && index - 1 >= 0) {
            this.keyRefs[index - 1] && this.keyRefs[index - 1].focus();
        }
    };

    private reportUp = (index: number) => () => {
        const value = this.keyRefs[index].value;
        const maxLength = KEY_DIGITS.length;
        // Handles pasting a code
        if (value.length >= maxLength) {
            this.props.resetCode(value.replace(/\D/g, "")?.substring(0, maxLength));
            return;
        }

        // OR handle the single entry
        // spreading the value handles multiple characters typed into a single input
        const val = [...value][value.length - 1];

        if (!isNaN(val) || val === undefined) {
            this.props.setCodeByIndex(index, val);
            if (val !== undefined) {
                const nextIndex = index + 1;
                if (nextIndex < this.keyRefs.length) {
                    this.keyRefs[nextIndex]?.focus();
                }
            }
        }
    };
}

export default withTranslation()(UnlockGarage);
